import { React, useState, useEffect } from "react";
import { Moon, Sun } from "lucide-react";
import { useCustomTheme } from "../theme-shadcn";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import OpacityIcon from "@mui/icons-material/Opacity";
import PaletteIcon from "@mui/icons-material/Palette";

import { Card, Typography, FormControl, Select, MenuItem } from "@mhome/ui";
import { useRecoilValue, useRecoilState } from "recoil";
import {
  platformState,
  backgroundTypeState,
  backgroundOpacityState,
  primaryColorState,
} from "../global-state";
import {
  useComponentBackgroundColor,
  getBackground,
} from "../common/adaptive-theme-provider";

const Theme = () => {
  const platform = useRecoilValue(platformState);

  const { mode, actualMode, setMode } = useCustomTheme();

  // Force re-render when theme changes by listening to themechange event
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const handleThemeChange = () => {
      forceUpdate((prev) => prev + 1);
    };

    window.addEventListener("themechange", handleThemeChange);
    return () => window.removeEventListener("themechange", handleThemeChange);
  }, []);

  // Background selection state - now using global state
  const [backgroundType, setBackgroundType] =
    useRecoilState(backgroundTypeState);
  const [backgroundOpacity, setBackgroundOpacity] = useRecoilState(
    backgroundOpacityState
  );
  const [primaryColor, setPrimaryColor] = useRecoilState(primaryColorState);

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  // Get background style
  const backgroundStyle = getBackground(backgroundType);

  // Background options
  const backgroundOptions = [
    { value: "default", label: "Default" },
    { value: "sky", label: "Sky" },
    { value: "aurora", label: "Aurora" },
    { value: "coral", label: "Coral Reef" },
    { value: "lavender", label: "Lavender Fields" },
    { value: "mint", label: "Mint Fresh" },
    { value: "rose", label: "Rose Garden" },
    { value: "galaxy", label: "Galaxy" },
    { value: "golden", label: "Golden Hour" },
  ];

  // Opacity options
  const opacityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "full", label: "Full" },
  ];

  // Color options
  const colorOptions = [
    { value: "default", label: "Default" },
    { value: "blue", label: "Blue" },
    { value: "purple", label: "Purple" },
    { value: "green", label: "Green" },
    { value: "orange", label: "Orange" },
    { value: "teal", label: "Teal" },
    { value: "pink", label: "Pink" },
  ];

  return (
    <div
      className="pl-4 pr-4 pt-4 pb-4 h-auto min-h-screen max-w-full box-border flex flex-col gap-3"
      style={{
        background: backgroundStyle || `hsl(var(--background))`,
      }}
    >
      {/* Theme Toggle Card */}
      <Card
        className="py-5 px-5 select-none"
        bgcolor={getComponentBgColor(`hsl(var(--card))`)}
        style={{
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <div className="flex items-center justify-between select-none">
          <div className="flex items-center gap-4 select-none">
            <div className="flex items-center select-none">
              {actualMode === "dark" ? <Moon /> : <Sun />}
              <div className="ml-2 select-none">
                <Typography
                  variant="h6"
                  color="default"
                  className="select-none"
                >
                  Theme
                </Typography>
                <Typography
                  variant="body2"
                  color="muted"
                  className="select-none"
                >
                  {mode === "auto"
                    ? `Auto (${actualMode === "dark" ? "Dark" : "Light"})`
                    : mode === "dark"
                    ? "Dark Theme"
                    : "Light Theme"}
                </Typography>
              </div>
            </div>
          </div>

          <FormControl size="small" className="min-w-[120px]">
            {platform?.source === "app" ? (
              <Select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                displayEmpty
                variant="outlined"
                className="select-none"
                style={{
                  backgroundColor: "transparent",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <MenuItem value="auto">
                  <div className="flex items-center gap-2 select-none">
                    <Typography className="select-none">Auto</Typography>
                  </div>
                </MenuItem>
              </Select>
            ) : (
              <Select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                displayEmpty
                variant="outlined"
                className="select-none"
                style={{
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <MenuItem value="light">
                  <div className="flex items-center gap-2 select-none">
                    <Typography className="select-none">Light</Typography>
                  </div>
                </MenuItem>
                <MenuItem value="dark">
                  <div className="flex items-center gap-2 select-none">
                    <Typography className="select-none">Dark</Typography>
                  </div>
                </MenuItem>
              </Select>
            )}
          </FormControl>
        </div>
      </Card>

      {/* Primary Color Selection Card */}
      <Card
        className="py-5 px-5 select-none"
        bgcolor={getComponentBgColor(`hsl(var(--card))`)}
        style={{
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <div className="flex items-center justify-between select-none">
          <div className="flex items-center gap-4 select-none">
            <div className="flex items-center select-none">
              <PaletteIcon style={{ marginRight: 8, userSelect: "none" }} />
              <div className="select-none">
                <Typography
                  variant="h6"
                  color="default"
                  className="select-none"
                >
                  Primary Color
                </Typography>
                <Typography
                  variant="body2"
                  color="muted"
                  className="select-none"
                >
                  {colorOptions.find((option) => option.value === primaryColor)
                    ?.label || "Default"}
                </Typography>
              </div>
            </div>
          </div>

          <FormControl size="small" className="min-w-[120px]">
            <Select
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              displayEmpty
              variant="outlined"
              className="select-none"
              style={{
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {colorOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2 select-none">
                    <Typography className="select-none">
                      {option.label}
                    </Typography>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </Card>

      {/* Background Selection Card */}
      <Card
        className="py-5 px-5 select-none"
        bgcolor={getComponentBgColor(`hsl(var(--card))`)}
        style={{
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <div className="flex items-center justify-between select-none">
          <div className="flex items-center gap-4 select-none">
            <div className="flex items-center select-none">
              <WallpaperIcon style={{ marginRight: 8, userSelect: "none" }} />
              <div className="select-none">
                <Typography
                  variant="h6"
                  color="default"
                  className="select-none"
                >
                  Background
                </Typography>
                <Typography
                  variant="body2"
                  color="muted"
                  className="select-none"
                >
                  {backgroundOptions.find(
                    (option) => option.value === backgroundType
                  )?.label || "Default"}
                </Typography>
              </div>
            </div>
          </div>

          <FormControl size="small" className="min-w-[120px]">
            <Select
              value={backgroundType}
              onChange={(e) => setBackgroundType(e.target.value)}
              displayEmpty
              variant="outlined"
              className="select-none"
              style={{
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {backgroundOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2 select-none">
                    <Typography className="select-none">
                      {option.label}
                    </Typography>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </Card>

      {/* Opacity Selection Card */}
      <Card
        className="py-5 px-5 select-none"
        bgcolor={getComponentBgColor(`hsl(var(--card))`)}
        style={{
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <div className="flex items-center justify-between select-none">
          <div className="flex items-center gap-4 select-none">
            <div className="flex items-center select-none">
              <OpacityIcon style={{ marginRight: 8, userSelect: "none" }} />
              <div className="select-none">
                <Typography
                  variant="h6"
                  color="default"
                  className="select-none"
                >
                  Opacity
                </Typography>
                <Typography
                  variant="body2"
                  color="muted"
                  className="select-none"
                >
                  Change component opacity when using on a background
                </Typography>
              </div>
            </div>
          </div>

          <FormControl size="small" className="min-w-[120px]">
            <Select
              value={backgroundOpacity}
              onChange={(e) => setBackgroundOpacity(e.target.value)}
              displayEmpty
              variant="outlined"
              className="select-none"
              style={{
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {opacityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2 select-none">
                    <Typography className="select-none">
                      {option.label}
                    </Typography>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </Card>
    </div>
  );
};

export default Theme;
